/**
 * Smart wrapper for the car icon that automatically adjusts
 * wheel and highlight colors based on the theme color
 */
import React from 'react';

import { NakedIcon, VectorIconProps } from 'theme/icons';
import { darken } from 'theme/utils/colorUtils';

export interface CarThemeIconProps extends Omit<VectorIconProps, 'name'> {
  /** The accent color for the car body */
  accentColor: string;
  /** Icon name override (defaults to 'car-48') */
  iconName?: string;
  /** Color prop for the icon (semi-transparent overlay) */
  color?: string;
  /** Fill color for the icon body */
  fill?: string;
}

/**
 * A themed car icon that automatically derives wheel and highlight colors
 * from the provided accent color.
 *
 * Wheels are rendered 20% darker than the accent color.
 * Highlights (headlights/taillights) are rendered 30% lighter.
 *
 * @param props - CarThemeIcon props
 * @returns Themed car icon component
 */
export function CarThemeIcon({
  accentColor,
  iconName = 'car-48',
  color,
  fill,
  ...props
}: CarThemeIconProps) {
  const wheelColor = darken(accentColor, 0.2);
  const highlightColor = 'white'; // Bright white for headlights/taillights

  return (
    <NakedIcon
      name={iconName}
      color={color}
      fill={fill}
      {...props}
      style={{
        ...(props.style || {}),
        '--wheel-color': wheelColor,
        '--highlight-color': highlightColor,
      }}
    />
  );
}
